import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cable, Circle, Ruler, Zap, Lightbulb } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GROUP_CONFIG = {
  connector: { label: 'Connector', icon: Cable },
  end_cap: { label: 'End Cap', icon: Circle },
  profile: { label: 'Aluminum Profile', icon: Ruler },
  driver: { label: 'Driver', icon: Zap },
};

const MATCH_LABEL = {
  'erp-linked': 'Fits this SKU',
  'series-match': 'Same series',
  'wattage-match': 'Sized for this load',
  'voltage-match': 'Matching voltage',
};

function BundleItem({ item }) {
  const href = item.canonical_slug
    ? `/${item.collection || 'lumi-flex'}/${item.canonical_slug}${item.base_code ? `?sku=${encodeURIComponent(item.base_code)}` : ''}`
    : `/products/${item.product_id}`;
  return (
    <Link
      to={href}
      className="flex items-center gap-3 border border-[#EEE] hover:border-[#1A1A1A] transition-colors p-3 bg-white"
      data-testid={`bundle-item-${item.product_id || item.canonical_slug}`}
    >
      <div className="w-12 h-12 flex-shrink-0 bg-[#FAFAFA] flex items-center justify-center overflow-hidden">
        {item.primary_image ? (
          <img src={item.primary_image} alt={item.name} className="w-full h-full object-contain" loading="lazy" />
        ) : (
          <Lightbulb className="w-5 h-5 text-[#CCC]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-[#1A1A1A] line-clamp-2">{item.name}</p>
        <p className="text-[10px] uppercase tracking-wider text-[#999] mt-0.5">{MATCH_LABEL[item.match_type] || 'Suggested'}</p>
      </div>
    </Link>
  );
}

export default function SmartBundlePanel({ baseCode }) {
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!baseCode) return;
    setLoading(true);
    fetch(`${BACKEND_URL}/api/search/smart-bundle/${baseCode}`)
      .then(r => (r.ok ? r.json() : null))
      .then(setBundle)
      .catch(() => setBundle(null))
      .finally(() => setLoading(false));
  }, [baseCode]);

  if (loading) return null;
  if (!bundle || bundle.total === 0) return null;

  const groupKeys = ['connector', 'end_cap', 'profile', 'driver'].filter(k => (bundle[k] || []).length > 0);
  if (groupKeys.length === 0) return null;

  return (
    <div className="mt-6 pt-6 border-t border-[#F0F0F0]" data-testid="smart-bundle-panel">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-1">Complete Your Setup</p>
      <p className="text-xs text-[#777] mb-4">Recommended accessories for <span className="font-semibold">{bundle.base_code}</span></p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {groupKeys.map(key => {
          const config = GROUP_CONFIG[key];
          const Icon = config.icon;
          return (
            <div key={key} data-testid={`bundle-group-${key}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-3.5 h-3.5 text-[#BBB]" />
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#666]">{config.label}</span>
              </div>
              <div className="space-y-2">
                {bundle[key].slice(0, 2).map(item => (
                  <BundleItem key={item.product_id || item.canonical_slug} item={item} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
